import {
  Button,
  Checkbox,
  Input,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from "@assetwatch/ui";
import { useTranslation } from "react-i18next";
import type { MotionAmplificationPreset } from "../../clients/motion-amplification";
import type {
  MotionAmplificationFormParameters,
  MotionAmplificationParameterField,
  MotionAmplificationParameterValidationError,
} from "./motionAmplificationParameters";
import {
  getMotionAmplificationParameterValidationErrors,
  isPresetCompatibleWithFps,
  nyquistFrequencyForFps,
  PRESET_DEFAULTS,
} from "./motionAmplificationParameters";

type UpdateParameter = <Key extends keyof MotionAmplificationFormParameters>(
  key: Key,
  value: MotionAmplificationFormParameters[Key],
) => void;

function ParameterValidationMessage({
  field,
  label,
  errors,
  t,
}: {
  field: MotionAmplificationParameterField;
  label: string;
  errors: MotionAmplificationParameterValidationError[];
  t: (key: string, options?: Record<string, unknown>) => string;
}) {
  const error = errors.find((item) => {
    if (item.field === field) return true;
    if (item.field === "frequencyBand" && field === "frequencyHighHz") {
      return true;
    }
    return item.field === "outputMode" && field === "outputMode";
  });
  if (!error) return null;

  const message =
    error.code === "required"
      ? t("motionAmplification.parameterRequired", { parameter: label })
      : error.code === "range"
        ? t("motionAmplification.parameterRange", {
            parameter: label,
            min: error.min,
            max: error.max,
          })
        : error.code === "positiveInteger"
          ? t("motionAmplification.parameterPositiveInteger", {
              parameter: label,
              max: error.max,
            })
          : error.code === "frequencyOrder"
            ? t("motionAmplification.frequencyOrderError")
            : error.code === "nyquist"
              ? t("motionAmplification.frequencyError", {
                  frequency: error.max?.toFixed(0),
                })
              : error.code === "chunkOverlap"
                ? t("motionAmplification.chunkOverlapError")
                : error.code === "cropCoordinate"
                  ? t("motionAmplification.cropCoordinateError")
                  : error.code === "cropDimension"
                    ? t("motionAmplification.cropDimensionError")
                    : t("motionAmplification.outputConflictError");

  return <Text tone="negative">{message}</Text>;
}

export function MotionAmplificationParameterForm({
  parameters,
  updateParameter,
  preset,
  onPresetChange,
  fps,
  showAdvanced,
  onToggleAdvanced,
}: {
  parameters: MotionAmplificationFormParameters;
  updateParameter: UpdateParameter;
  preset: MotionAmplificationPreset;
  onPresetChange: (value: string | null) => void;
  fps: number | null;
  showAdvanced: boolean;
  onToggleAdvanced: () => void;
}) {
  const { t } = useTranslation();
  const maxFrequencyHz = nyquistFrequencyForFps(fps);
  const validationErrors = getMotionAmplificationParameterValidationErrors(
    parameters,
    fps,
  );
  function setOutputOption(option: "heatmap" | "sideBySide", checked: boolean) {
    updateParameter(option, checked);
    if (checked) {
      updateParameter(option === "heatmap" ? "sideBySide" : "heatmap", false);
    }
  }
  const setCrop = (key: "x" | "y" | "width" | "height", value: number | null) =>
    updateParameter(
      "crop",
      value === null
        ? null
        : {
            x: 0,
            y: 0,
            width: 1,
            height: 1,
            ...(parameters.crop ?? {}),
            [key]: value,
          },
    );
  const numberInput = (
    label: string,
    value: number | null,
    onChange: (value: number | null) => void,
    props: { min?: number; max?: number; step?: number } = {},
  ) => (
    <Input
      aria-label={label}
      type="number"
      value={value ?? ""}
      onChange={(event) =>
        onChange(event.target.value === "" ? null : Number(event.target.value))
      }
      reserveSubtextSpace={false}
      {...props}
    />
  );
  return (
    <Stack gap={16}>
      <Text>{t("motionAmplification.processingParameters")}</Text>
      <div className="[&_thead_th]:bg-surface-container [&_tbody_tr_td]:py-(--spacing-4)">
        <Table size="sm">
          <TableHeader>
            <TableRow section="header">
              <TableHead scope="col">
                {t("motionAmplification.parameter")}
              </TableHead>
              <TableHead scope="col">
                {t("motionAmplification.value")}
              </TableHead>
              <TableHead scope="col">
                {t("motionAmplification.parameterDescription")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell>{t("motionAmplification.preset")}</TableCell>
              <TableCell>
                <Select
                  options={Object.keys(PRESET_DEFAULTS).map((value) => ({
                    value,
                    label: t(`motionAmplification.presets.${value}`),
                  }))}
                  value={preset}
                  reserveSubtextSpace={false}
                  onChange={(value) => {
                    onPresetChange(value);
                  }}
                />
              </TableCell>
              <TableCell>
                {t("motionAmplification.descriptions.preset")}
                {fps !== null && !isPresetCompatibleWithFps(preset, fps) && (
                  <Text tone="negative">
                    {t("motionAmplification.presetFpsError")}
                  </Text>
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t("motionAmplification.frequencyStart")}</TableCell>
              <TableCell>
                {numberInput(
                  t("motionAmplification.frequencyStart"),
                  parameters.frequencyLowHz,
                  (v) => updateParameter("frequencyLowHz", v),
                  { min: 0.5, max: 100, step: 0.5 },
                )}
              </TableCell>
              <TableCell>
                {t("motionAmplification.descriptions.frequencyStart")}
                <ParameterValidationMessage
                  field="frequencyLowHz"
                  label={t("motionAmplification.frequencyStart")}
                  errors={validationErrors}
                  t={t}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t("motionAmplification.frequencyEnd")}</TableCell>
              <TableCell>
                {numberInput(
                  t("motionAmplification.frequencyEnd"),
                  parameters.frequencyHighHz,
                  (v) => updateParameter("frequencyHighHz", v),
                  { min: 0.5, max: maxFrequencyHz, step: 0.5 },
                )}
              </TableCell>
              <TableCell>
                {t("motionAmplification.descriptions.frequencyEnd")}
                <ParameterValidationMessage
                  field="frequencyHighHz"
                  label={t("motionAmplification.frequencyEnd")}
                  errors={validationErrors}
                  t={t}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t("motionAmplification.alpha")}</TableCell>
              <TableCell>
                {numberInput(
                  t("motionAmplification.alpha"),
                  parameters.alpha,
                  (v) => updateParameter("alpha", v),
                  { min: 0, max: 1000 },
                )}
              </TableCell>
              <TableCell>
                {t("motionAmplification.descriptions.alpha")}
                <ParameterValidationMessage
                  field="alpha"
                  label={t("motionAmplification.alpha")}
                  errors={validationErrors}
                  t={t}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t("motionAmplification.lambdaC")}</TableCell>
              <TableCell>
                {numberInput(
                  t("motionAmplification.lambdaC"),
                  parameters.lambdaC,
                  (v) => updateParameter("lambdaC", v),
                  { min: 0, max: 1000 },
                )}
              </TableCell>
              <TableCell>
                {t("motionAmplification.descriptions.lambdaC")}
                <ParameterValidationMessage
                  field="lambdaC"
                  label={t("motionAmplification.lambdaC")}
                  errors={validationErrors}
                  t={t}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                {t("motionAmplification.stabilizationSmoothing")}
              </TableCell>
              <TableCell>
                {numberInput(
                  t("motionAmplification.stabilizationSmoothing"),
                  parameters.stabilizeSmooth,
                  (v) => updateParameter("stabilizeSmooth", v),
                  { min: 1, max: 1001, step: 2 },
                )}
              </TableCell>
              <TableCell>
                {t("motionAmplification.descriptions.stabilizationSmoothing")}
                <ParameterValidationMessage
                  field="stabilizeSmooth"
                  label={t("motionAmplification.stabilizationSmoothing")}
                  errors={validationErrors}
                  t={t}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t("motionAmplification.maximumWidth")}</TableCell>
              <TableCell>
                {numberInput(
                  t("motionAmplification.maximumWidth"),
                  parameters.maxWidth,
                  (v) => updateParameter("maxWidth", v),
                  { min: 1, max: 4096 },
                )}
              </TableCell>
              <TableCell>
                {t("motionAmplification.descriptions.maximumWidth")}
                <ParameterValidationMessage
                  field="maxWidth"
                  label={t("motionAmplification.maximumWidth")}
                  errors={validationErrors}
                  t={t}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>{t("motionAmplification.stabilization")}</TableCell>
              <TableCell>
                <Checkbox
                  label={t("motionAmplification.stabilizeVideo")}
                  checked={parameters.stabilize}
                  onCheckedChange={(checked) =>
                    updateParameter("stabilize", checked === true)
                  }
                />
              </TableCell>
              <TableCell>
                {t("motionAmplification.descriptions.stabilization")}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
      <Button
        type="button"
        variant="ghost"
        onClick={() => {
          onToggleAdvanced();
        }}
      >
        {showAdvanced
          ? t("motionAmplification.hideAdvancedSettings")
          : t("motionAmplification.advancedSettings")}
      </Button>
      {showAdvanced && (
        <Stack gap={16}>
          <Text>{t("motionAmplification.advancedSettings")}</Text>
          <div className="[&_thead_th]:bg-surface-container [&_tbody_tr_td]:py-(--spacing-4)">
            <Table size="sm">
              <TableHeader>
                <TableRow section="header">
                  <TableHead scope="col">
                    {t("motionAmplification.parameter")}
                  </TableHead>
                  <TableHead scope="col">
                    {t("motionAmplification.value")}
                  </TableHead>
                  <TableHead scope="col">
                    {t("motionAmplification.parameterDescription")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell>
                    {t("motionAmplification.pyramidLevels")}
                  </TableCell>
                  <TableCell>
                    {numberInput(
                      t("motionAmplification.pyramidLevels"),
                      parameters.pyramidLevels,
                      (v) => updateParameter("pyramidLevels", v),
                      { min: 1, max: 12 },
                    )}
                  </TableCell>
                  <TableCell>
                    {t("motionAmplification.descriptions.pyramidLevels")}
                    <ParameterValidationMessage
                      field="pyramidLevels"
                      label={t("motionAmplification.pyramidLevels")}
                      errors={validationErrors}
                      t={t}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    {t("motionAmplification.chromaAttenuation")}
                  </TableCell>
                  <TableCell>
                    {numberInput(
                      t("motionAmplification.chromaAttenuation"),
                      parameters.chromAttenuation,
                      (v) => updateParameter("chromAttenuation", v),
                      { min: 0, max: 1, step: 0.05 },
                    )}
                  </TableCell>
                  <TableCell>
                    {t("motionAmplification.descriptions.chromaAttenuation")}
                    <ParameterValidationMessage
                      field="chromAttenuation"
                      label={t("motionAmplification.chromaAttenuation")}
                      errors={validationErrors}
                      t={t}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t("motionAmplification.chunkSize")}</TableCell>
                  <TableCell>
                    {numberInput(
                      t("motionAmplification.chunkSize"),
                      parameters.chunkSize,
                      (v) => updateParameter("chunkSize", v),
                      { min: 1, max: 2048 },
                    )}
                  </TableCell>
                  <TableCell>
                    {t("motionAmplification.descriptions.chunkSize")}
                    <ParameterValidationMessage
                      field="chunkSize"
                      label={t("motionAmplification.chunkSize")}
                      errors={validationErrors}
                      t={t}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t("motionAmplification.chunkOverlap")}</TableCell>
                  <TableCell>
                    {numberInput(
                      t("motionAmplification.chunkOverlap"),
                      parameters.overlap,
                      (v) => updateParameter("overlap", v),
                      {
                        min: 1,
                        max:
                          parameters.chunkSize === null
                            ? 2047
                            : parameters.chunkSize - 1,
                      },
                    )}
                  </TableCell>
                  <TableCell>
                    {t("motionAmplification.descriptions.chunkOverlap")}
                    <ParameterValidationMessage
                      field="overlap"
                      label={t("motionAmplification.chunkOverlap")}
                      errors={validationErrors}
                      t={t}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    {t("motionAmplification.stabilizationPasses")}
                  </TableCell>
                  <TableCell>
                    {numberInput(
                      t("motionAmplification.stabilizationPasses"),
                      parameters.stabilizeSmoothPasses,
                      (v) => updateParameter("stabilizeSmoothPasses", v),
                      { min: 1, max: 10 },
                    )}
                  </TableCell>
                  <TableCell>
                    {t("motionAmplification.descriptions.stabilizationPasses")}
                    <ParameterValidationMessage
                      field="stabilizeSmoothPasses"
                      label={t("motionAmplification.stabilizationPasses")}
                      errors={validationErrors}
                      t={t}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    {t("motionAmplification.maximumFeatures")}
                  </TableCell>
                  <TableCell>
                    {numberInput(
                      t("motionAmplification.maximumFeatures"),
                      parameters.stabilizeMaxFeatures,
                      (v) => updateParameter("stabilizeMaxFeatures", v),
                      { min: 1, max: 5000 },
                    )}
                  </TableCell>
                  <TableCell>
                    {t("motionAmplification.descriptions.maximumFeatures")}
                    <ParameterValidationMessage
                      field="stabilizeMaxFeatures"
                      label={t("motionAmplification.maximumFeatures")}
                      errors={validationErrors}
                      t={t}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    {t("motionAmplification.ransacThreshold")}
                  </TableCell>
                  <TableCell>
                    {numberInput(
                      t("motionAmplification.ransacThreshold"),
                      parameters.stabilizeRansacPx,
                      (v) => updateParameter("stabilizeRansacPx", v),
                      { min: 0.1, max: 100, step: 0.1 },
                    )}
                  </TableCell>
                  <TableCell>
                    {t("motionAmplification.descriptions.ransacThreshold")}
                    <ParameterValidationMessage
                      field="stabilizeRansacPx"
                      label={t("motionAmplification.ransacThreshold")}
                      errors={validationErrors}
                      t={t}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    {t("motionAmplification.fastThreshold")}
                  </TableCell>
                  <TableCell>
                    {numberInput(
                      t("motionAmplification.fastThreshold"),
                      parameters.stabilizeFastThreshold,
                      (v) => updateParameter("stabilizeFastThreshold", v),
                      { min: 1, max: 255 },
                    )}
                  </TableCell>
                  <TableCell>
                    {t("motionAmplification.descriptions.fastThreshold")}
                    <ParameterValidationMessage
                      field="stabilizeFastThreshold"
                      label={t("motionAmplification.fastThreshold")}
                      errors={validationErrors}
                      t={t}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    {t("motionAmplification.heatmapOpacity")}
                  </TableCell>
                  <TableCell>
                    {numberInput(
                      t("motionAmplification.heatmapOpacity"),
                      parameters.heatmapAlpha,
                      (v) => updateParameter("heatmapAlpha", v),
                      { min: 0, max: 1, step: 0.05 },
                    )}
                  </TableCell>
                  <TableCell>
                    {t("motionAmplification.descriptions.heatmapOpacity")}
                    <ParameterValidationMessage
                      field="heatmapAlpha"
                      label={t("motionAmplification.heatmapOpacity")}
                      errors={validationErrors}
                      t={t}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t("motionAmplification.cropX")}</TableCell>
                  <TableCell>
                    {numberInput(
                      t("motionAmplification.cropX"),
                      parameters.crop?.x ?? null,
                      (v) => setCrop("x", v),
                      { min: 0 },
                    )}
                  </TableCell>
                  <TableCell>
                    {t("motionAmplification.descriptions.cropX")}
                    <ParameterValidationMessage
                      field="cropX"
                      label={t("motionAmplification.cropX")}
                      errors={validationErrors}
                      t={t}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t("motionAmplification.cropY")}</TableCell>
                  <TableCell>
                    {numberInput(
                      t("motionAmplification.cropY"),
                      parameters.crop?.y ?? null,
                      (v) => setCrop("y", v),
                      { min: 0 },
                    )}
                  </TableCell>
                  <TableCell>
                    {t("motionAmplification.descriptions.cropY")}
                    <ParameterValidationMessage
                      field="cropY"
                      label={t("motionAmplification.cropY")}
                      errors={validationErrors}
                      t={t}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t("motionAmplification.cropWidth")}</TableCell>
                  <TableCell>
                    {numberInput(
                      t("motionAmplification.cropWidth"),
                      parameters.crop?.width ?? null,
                      (v) => setCrop("width", v),
                      { min: 1 },
                    )}
                  </TableCell>
                  <TableCell>
                    {t("motionAmplification.descriptions.cropWidth")}
                    <ParameterValidationMessage
                      field="cropWidth"
                      label={t("motionAmplification.cropWidth")}
                      errors={validationErrors}
                      t={t}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>{t("motionAmplification.cropHeight")}</TableCell>
                  <TableCell>
                    {numberInput(
                      t("motionAmplification.cropHeight"),
                      parameters.crop?.height ?? null,
                      (v) => setCrop("height", v),
                      { min: 1 },
                    )}
                  </TableCell>
                  <TableCell>
                    {t("motionAmplification.descriptions.cropHeight")}
                    <ParameterValidationMessage
                      field="cropHeight"
                      label={t("motionAmplification.cropHeight")}
                      errors={validationErrors}
                      t={t}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    {t("motionAmplification.heatmapOutput")}
                  </TableCell>
                  <TableCell>
                    <Checkbox
                      label={t("motionAmplification.heatmapOutput")}
                      checked={parameters.heatmap}
                      disabled={parameters.sideBySide}
                      onCheckedChange={(checked) =>
                        setOutputOption("heatmap", checked === true)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {t("motionAmplification.descriptions.heatmapOutput")}
                    <ParameterValidationMessage
                      field="outputMode"
                      label={t("motionAmplification.heatmapOutput")}
                      errors={validationErrors}
                      t={t}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>
                    {t("motionAmplification.sideBySideOutput")}
                  </TableCell>
                  <TableCell>
                    <Checkbox
                      label={t("motionAmplification.sideBySideOutput")}
                      checked={parameters.sideBySide}
                      disabled={parameters.heatmap}
                      onCheckedChange={(checked) =>
                        setOutputOption("sideBySide", checked === true)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {t("motionAmplification.descriptions.sideBySideOutput")}
                    <ParameterValidationMessage
                      field="outputMode"
                      label={t("motionAmplification.sideBySideOutput")}
                      errors={validationErrors}
                      t={t}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </Stack>
      )}
    </Stack>
  );
}
